import { Resizable } from 're-resizable';
import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import InitialsIcon from '../../../assets/icons/initials.svg';
import SignatureIcon from '../../../assets/icons/signature.svg';
import { FileContext } from '../../../context/FileContext';
import { useColumnOrder } from '../../../hooks/useColumnOrder';
import { isSignerEligibleColumn } from '../../../utils/signer-column-eligibility';

const Icon = styled.img`
  margin-right: ${({ noPadding }) => (noPadding ? '0' : '10px')};
`;

const SignatureField = styled.div`
  font-size: ${({ fontSize }) => fontSize + 'px'};
  display: flex;
  align-items: center;
  padding-left: ${({ noPadding }) => (noPadding ? '0' : '8px')};
  padding-right: ${({ noPadding }) => (noPadding ? '0' : '8px')};
  border: 1px solid #c5c7d0 !important;
  border-radius: 4px !important;
  width: 100%;
  height: 100%;
  background: ${({ backgroundColor }) => backgroundColor};
  color: black;
  line-height: 1.4;

  div {
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CustomContainer = styled.div`
  padding: ${({ noPadding }) => (noPadding ? '0' : '2px')};
  font-size: 11px;
  font-weight: 400;
  border: 4px solid transparent !important;
  border-radius: 4px !important;

  &.active {
    border: 4px solid rgba(0, 115, 234, 0.4) !important;
    border-radius: 4px !important;
  }

  &.hover-active:hover {
    border: 4px solid rgba(0, 115, 234, 0.4) !important;
    border-radius: 4px !important;
  }
`;

const SignatureAndInitialsEditChip = ({ item, setIsResizing }) => {
  const defaultHeight = 26.66;
  const defaultWidth = 183.33;
  const defaultFontSize = 11;
  const { rgbMapper } = useColumnOrder();
  const noPadding = item.noPadding || false;
  const itemColor = item.color ?? item.signer?.color;
  const backgroundColor = itemColor
    ? rgbMapper[itemColor] ?? itemColor
    : '#E6E6FA';

  const {
    addedFormFields,
    updateFormFields,
    currentSelectedItem,
    boardData,
    draggableFieldsData,
  } = useContext(FileContext);
  const { t } = useTranslation();

  const handleResizeStart = (_e, _dir) => {
    setIsResizing(true);
  };

  const handleResizeEnd = (item) => (_e, _dir, _ref, d) => {
    setIsResizing(false);

    item.width = _ref.clientWidth;
    item.height = _ref.clientHeight;

    addedFormFields[currentSelectedItem.current] = item;

    draggableFieldsData[item.itemId] = {
      height: item.height,
      width: item.width,
    };
    updateFormFields([...addedFormFields]);
  };

  // handle email label if changed from monday board
  const emailColumns = boardData?.columns?.filter((col) =>
    isSignerEligibleColumn(col),
  );
  const getTitle = useCallback(
    (id) => {
      if (!emailColumns) return;
      const title = emailColumns?.find((x) => x.id === id)?.title || '';
      return title;
    },
    [emailColumns],
  );

  const label = useMemo(() => {
    if (item?.signer?.label) {
      return item.signer.label === 'Me'
        ? item.signer.label
        : getTitle(item.signer.emailColumnId);
    }

    return item?.title?.includes('Signature') ? t('Signature') : t('Initials');
  }, [item, t, getTitle]);

  return (
    <CustomContainer
      className={item.isActive ? 'active' : 'hover-active'}
      noPadding={noPadding}
    >
      <Resizable
        size={{
          height: item?.height || defaultHeight,
          width: item?.width || defaultWidth,
        }}
        lockAspectRatio={false}
        enable={{
          top: false,
          right: item?.isActive,
          bottom: item?.isActive,
          left: false,
          topRight: false,
          bottomRight: item?.isActive,
          bottomLeft: false,
          topLeft: false,
        }}
        className={item.isActive ? 'resizeable' : ''}
        onResizeStart={handleResizeStart}
        onResizeStop={handleResizeEnd(item)}
      >
        <SignatureField
          backgroundColor={backgroundColor}
          fontSize={defaultFontSize}
          noPadding={noPadding}
        >
          <div>
            {item?.title.includes('Signature') && (
              <Icon
                src={SignatureIcon}
                alt=""
                height={16}
                width={16}
                noPadding={noPadding}
              />
            )}
            {item?.title.includes('Initials') && (
              <Icon
                src={InitialsIcon}
                alt=""
                height={16}
                width={16}
                noPadding={noPadding}
              />
            )}
            {label}
          </div>
        </SignatureField>
      </Resizable>
    </CustomContainer>
  );
};

export default SignatureAndInitialsEditChip;
